import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import classNames from "classnames";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import { Title, Button, Text, Field, Image, PopOverList } from "components/commons";
import { InputAmount } from "components/field";
import { getStations } from "apis";
import { useApi, useModal, usePagination, useFilter } from "hooks";
import { formatCurrency, parseNumber } from "utils";
import Validation from "utils/validation.utils";
import locale from "localization";
import { RemoveIcon } from "images";

import styles from "./pricing.module.scss";
import TargetStationsModal from "../details/account-details/target-stations-modal";
import ImportStationModal from "../details/account-details/import-station";
import { TargetStationsFilterState } from "../details/account-details/target-stations-filter.state";

// Blank discount-combination row (stations + per-product discount). Mirrors the
// onboarding row shape used by account-details so the same payload transform applies.
export const createDiscountRow = () => ({
  batchId: null,
  stationIds: {
    name: "stationIds",
    visible: false,
    label: locale.eligibleReferenceStation,
    allLabel: locale.referenceStation,
    isAll: true,
    isMultiple: true,
    value: [],
    placeholder: locale.searchOrSelect,
    disableAllOption: true,
    selectAllLabel: locale.all,
    allSelectedText: `All ${locale.referenceStation}`,
  },
  discountDiesel: {
    name: "discountDiesel",
    required: true,
    validations: [Validation.required({ noMessage: true })],
    innerLabel: true,
    parseNumber: true,
    value: "",
    placeholder: "0.00",
  },
  discountGas91: {
    name: "discountGas91",
    required: true,
    validations: [Validation.required({ noMessage: true })],
    innerLabel: true,
    parseNumber: true,
    value: "",
    placeholder: "0.00",
  },
  discountGas95: {
    name: "discountGas95",
    required: true,
    validations: [Validation.required({ noMessage: true })],
    innerLabel: true,
    parseNumber: true,
    value: "",
    placeholder: "0.00",
  },
  discountGas97: {
    name: "discountGas97",
    required: true,
    validations: [Validation.required({ noMessage: true })],
    innerLabel: true,
    parseNumber: true,
    value: "",
    placeholder: "0.00",
  },
});

// Seed the grid from a fetched fleet account. getFleet returns each combination with a
// full `stations` array: { stationId, stationCode, name, businessName }. All station
// display for a combination comes from there (falls back to plain stationIds if needed).
const buildDiscountCombination = (combos = []) => {
  // No saved discounts means an empty grid. A blank starter row used to stand in for it, but
  // it was indistinguishable from a row the user had emptied out, so neither could be
  // validated. Rows now only exist once "Add New Discount Combination" is clicked. [PL-8988]
  return (combos || []).map((combo) => {
    const row = createDiscountRow();
    row.batchId = combo.batchId ?? null;
    row.stationIds.value = (combo.stations || combo.stationIds || []).map((s) =>
      typeof s === "object"
        ? {
            value: s.stationId ?? s.value ?? s.id,
            label: s.name ?? s.businessName ?? s.stationName ?? s.label ?? s.stationId,
          }
        : { value: s, label: `${s}` }
    );
    row.discountDiesel.value = combo.discountDiesel ?? "";
    row.discountGas91.value = combo.discountGas91 ?? "";
    row.discountGas95.value = combo.discountGas95 ?? "";
    row.discountGas97.value = combo.discountGas97 ?? "";
    return row;
  });
};

// Transform for the updateFleetAccount (ms-fleet) payload; only rows with stations.
// batchId identifies which combination to replace: preserve it for existing rows and
// assign fresh ids to new rows so they never collide with an existing batch.
//
// Amounts go out as numbers: InputAmount's blur handler formats the edited field into a
// grouped string ("999,999,999,999.99"), and ms-fleet stores that verbatim. Untouched fields
// still held the plain number from the API, so only whichever field the user last edited was
// corrupted. [PL-8988]
//
// ms-fleet deletes and recreates only the batches the payload carries, so simply dropping a
// combination from the list leaves it saved. Removed batches are therefore sent as explicit
// tombstones (same batchId, no stations): the service deletes the batch and, with nothing to
// iterate over, recreates nothing. Tombstones go first and their ids stay out of the pool for
// new rows so a freshly added combination is never deleted by a later tombstone. [PL-8978]
const toFleetStationDiscounts = (combos, seededBatchIds = []) => {
  const valid = (combos || []).filter((row) => row?.stationIds?.value?.length > 0);
  const keptBatchIds = valid
    .map((row) => row.batchId)
    .filter((batchId) => batchId !== null && batchId !== undefined);
  const removedBatchIds = seededBatchIds.filter((batchId) => !keptBatchIds.includes(batchId));
  const usedBatchIds = [...keptBatchIds, ...removedBatchIds];
  let nextBatchId = usedBatchIds.length ? Math.max(...usedBatchIds) + 1 : 0;
  const tombstones = removedBatchIds.map((batchId) => ({
    batchId,
    stationIds: [],
    discountDiesel: 0,
    discountGas91: 0,
    discountGas95: 0,
    discountGas97: 0,
  }));
  return [
    ...tombstones,
    ...valid.map((item) => ({
      batchId: item.batchId ?? nextBatchId++,
      stationIds: item.stationIds.value.map((station) => station.value),
      discountDiesel: parseNumber(item.discountDiesel.value),
      discountGas91: parseNumber(item.discountGas91.value),
      discountGas95: parseNumber(item.discountGas95.value),
      discountGas97: parseNumber(item.discountGas97.value),
    })),
  ];
};

// Amounts arrive from the API as numbers (1) and leave the inputs as formatted strings
// ("1.00"), so the snapshot compares them numerically. Comparing raw strings made merely
// focusing and blurring a field look like an edit and enabled Save. [PL-8988]
const normalizeAmount = (value) =>
  value === "" || value === null || value === undefined ? "" : `${parseNumber(value)}`;

const simplifyCombos = (combos) =>
  JSON.stringify(
    (combos || []).map((r) => ({
      s: (r.stationIds?.value || []).map((x) => x.value).sort(),
      dd: normalizeAmount(r.discountDiesel?.value),
      g1: normalizeAmount(r.discountGas91?.value),
      g5: normalizeAmount(r.discountGas95?.value),
      g7: normalizeAmount(r.discountGas97?.value),
    }))
  );

const AMOUNT_KEYS = ["discountDiesel", "discountGas91", "discountGas95", "discountGas97"];

// The shared station pickers still push their selection into a parent-owned list. Here the
// claimed stations are derived from the grid instead, so the setter has nothing to do.
const noop = () => {};

const ContractDiscounts = forwardRef(
  ({ isEdit, loading, fleetStationDiscounts, onDirtyChange, onValidChange }, ref) => {
    const editMode = isEdit;

    const [discountCombination, setDiscountCombination] = useState([]);
    // Snapshot string of the seeded combinations. Storing a string (not the objects)
    // keeps the baseline immune to in-place edits of the current combinations.
    const [previousSimplified, setPreviousSimplified] = useState(() => simplifyCombos([]));
    const [stationInputs, setStationInputs] = useState([]);
    const [isAllStation, setIsAllStation] = useState({ checked: false });
    const [inputStationCount, setInputStationCount] = useState(0);
    const [stationslist, setStationsList] = useState([]);

    const stationsModal = useModal();
    const importStationModal = useModal();

    const {
      filterState,
      requestState,
      submitFilter,
      modifyFilters,
      filterCount,
      modifyFilter,
      clearFilter,
      submittedFilter,
    } = useFilter(TargetStationsFilterState());

    const { request: getStationsRequest, loading: loadingStations } = useApi({
      api: getStations,
      modalError: true,
      params: {
        platformType: "plb",
      },
    });

    const stationData = usePagination();

    // batchIds of the combinations currently saved on the account. The payload needs them to
    // tell ms-fleet which batches the user removed. [PL-8978]
    const seededBatchIdsRef = useRef([]);

    // Seed from the fetched account (view + edit). Also runs for an empty list so that
    // removing every discount is reflected after the account is refetched. Reused by reset()
    // so cancelling an edit drops the unsaved rows instead of leaving them on screen. [PL-8978]
    const seedCombinations = useCallback(() => {
      const seeded = buildDiscountCombination(fleetStationDiscounts || []);
      seededBatchIdsRef.current = seeded
        .map((row) => row.batchId)
        .filter((batchId) => batchId !== null && batchId !== undefined);
      setDiscountCombination(seeded);
      setPreviousSimplified(simplifyCombos(seeded));
      setStationInputs([]);
    }, [fleetStationDiscounts]);

    useEffect(() => {
      seedCombinations();
    }, [seedCombinations]);

    const discountCombinationChanged = simplifyCombos(discountCombination) !== previousSimplified;

    // Stations already claimed by a discount row, tagged with the row that owns them. The
    // station-picker greys out any entry whose owning row is not the one being edited, so a
    // station can belong to only one contract discount. Derived from the grid (not tracked in
    // separate state) so rows seeded from the saved account block re-selection too. [PL-8977]
    const stationsSelected = useMemo(
      () =>
        discountCombination.flatMap((row, rowIndex) => {
          const stations = row?.stationIds?.value || [];
          const claimed = stations.map(({ label, value }) => ({ label, value, id: rowIndex }));
          // Sentinel the picker checks to disable its "select all" header for other rows.
          if (inputStationCount > 0 && stations.length >= inputStationCount) {
            claimed.push({ label: "isAll", value: 0, id: rowIndex });
          }
          return claimed;
        }),
      [discountCombination, inputStationCount]
    );

    // Every row on the grid must carry a station AND all four product discounts; 0.00 counts as
    // a discount, only a blank does not. Emptying a row is no longer a way to clear a
    // combination — the X removes it outright — so an incomplete row always blocks Save while
    // an empty grid (every row removed) is perfectly valid. [PL-8988]
    const hasInvalidRow = discountCombination.some(
      (item) =>
        item.stationIds.value.length === 0 ||
        AMOUNT_KEYS.some((key) => item[key].value === "" || item[key].value === null)
    );

    // Enable Save only when contract discounts have complete, changed rows.
    useEffect(() => {
      if (onDirtyChange) onDirtyChange(discountCombinationChanged && !hasInvalidRow);
    }, [discountCombinationChanged, hasInvalidRow, onDirtyChange]);

    // Reported separately from dirtiness: an incomplete row has to block Save even when the
    // pricing form alone is what changed. [PL-8988]
    useEffect(() => {
      if (onValidChange) onValidChange(!hasInvalidRow);
    }, [hasInvalidRow, onValidChange]);

    useImperativeHandle(
      ref,
      () => ({
        getFleetStationDiscounts: () =>
          toFleetStationDiscounts(discountCombination, seededBatchIdsRef.current),
        hasChanges: () => discountCombinationChanged && !hasInvalidRow,
        reset: seedCombinations,
      }),
      [discountCombination, discountCombinationChanged, hasInvalidRow, seedCombinations]
    );

    const fetchStations = useCallback(
      async (rs = { ...requestState }, perPage) => {
        if (rs.reset) {
          stationData.reset();
        }
        if (stationData.canLoadMore || rs.reset) {
          const result = await getStationsRequest(
            submitFilter({
              perPage: perPage ? perPage : 10,
              ...rs,
              page: rs.reset ? 1 : stationData.page,
            }),
            null
          );
          stationData.onLoadMore({
            data: result.stations,
            total: result.count,
            resetPage: rs.reset,
          });
          if (inputStationCount === 0) {
            setInputStationCount(result?.count);
            setStationsList(result?.stations);
          }
          return result;
        }
      },
      [getStationsRequest, requestState, inputStationCount, submitFilter, stationData]
    );

    const selectStationsCb = useCallback(
      (stationParams) => {
        let newStations = [];
        let isAllSelected = false;
        const stationSelected = stationInputs.some(
          (station) => station.value === stationParams.value
        );
        if (stationSelected) {
          newStations = stationInputs.filter((station) => station.value !== stationParams.value);
        } else {
          newStations = [...stationInputs, stationParams];
        }
        if (inputStationCount === newStations.length) {
          isAllSelected = true;
        }
        setIsAllStation({ checked: isAllSelected });
        setStationInputs(newStations);
      },
      [inputStationCount, stationInputs]
    );

    const onClearSelectedStation = useCallback(
      (index) => {
        const newForm = [...discountCombination];
        newForm[index]["stationIds"].value = [];
        setDiscountCombination(newForm);
        setStationInputs([]);
      },
      [discountCombination]
    );

    const saveStationsFromCSVCb = useCallback(
      (index, selectedStations) => {
        const newForm = [...discountCombination];
        newForm[index]["stationIds"].value = selectedStations;
        setDiscountCombination(newForm);
        setStationInputs(selectedStations);
      },
      [discountCombination]
    );

    const saveStationsCb = useCallback(
      (index) => {
        const newForm = [...discountCombination];
        newForm[index]["stationIds"].value = stationInputs;
        setDiscountCombination(newForm);
      },
      [stationInputs, discountCombination]
    );

    const addRow = () => setDiscountCombination([...discountCombination, createDiscountRow()]);

    // Removing every row leaves an empty grid, which serializes to tombstones only (= no
    // contract discounts) and reads back as "-" on the view page. [PL-8978] [PL-8988]
    const onRemoveRow = (i) => {
      setDiscountCombination((prev) => prev.filter((_, index) => index !== i));
      setStationInputs([]);
    };

    const onChangeDiscount = (key, value, i) => {
      const newForm = [...discountCombination];
      newForm[i][key].value = value;
      setDiscountCombination(newForm);
    };

    const targetStationsProps = {
      filterState,
      modifyFilters,
      filterCount,
      modifyFilter,
      clearFilter,
      selectStationsCb,
      stationInputs,
      setStationInputs,
      saveStationsCb,
      fetchStations,
      loadingStations,
      isAllStation,
      setIsAllStation,
      inputStationCount,
      stationslist,
      setStationsList,
      submittedFilter,
      stationData,
      onClearSelectedStation,
      stationsSelected,
      setStationsSelected: noop,
    };

    const rowsToRender = editMode
      ? discountCombination
      : discountCombination.filter((row) => row?.stationIds?.value?.length > 0);

    // Product column: label on top, editable input (edit) or read-only value (view).
    const discountField = (item, i, key, label) => (
      <Field
        className={classNames(styles.fieldLabelGas, styles.fieldPricingGasLabel)}
        label={label}
      >
        {editMode ? (
          <InputAmount
            {...item[key]}
            onChange={(_, { value }) => onChangeDiscount(key, value, i)}
            className={styles.gasAmount}
            disabled={loading}
            disallowNegative
          />
        ) : (
          <Text className={styles.contractDiscountValue}>{formatCurrency(item[key].value)}</Text>
        )}
      </Field>
    );

    return (
      <div className={styles.container}>
        <TargetStationsModal {...stationsModal} {...targetStationsProps} />
        <ImportStationModal
          {...importStationModal}
          request={""}
          stationData={stationData}
          setStationInputs={setStationInputs}
          saveStationsCb={saveStationsFromCSVCb}
          stationInputs={stationInputs}
          discountCombination={discountCombination}
          stationsSelected={stationsSelected}
          setStationsSelected={noop}
          isHedging={false}
        />

        <Title xsmall>{locale.contractDiscounts}</Title>
        {!editMode && rowsToRender.length === 0 && <Text className={styles.fieldText}>-</Text>}
        <div className={styles.contractDiscountRows}>
          {rowsToRender.map((item, i) => {
            const stationCount = item?.stationIds?.value?.length || 0;
            const stationText = (
              <locale.Populate
                text={stationCount <= 1 ? "{0} Station" : locale.stationsEligibleSelected}
                items={[stationCount]}
              />
            );
            return (
              <div className={styles.stationReference} key={i}>
                {editMode ? (
                  stationCount === 0 ? (
                    <>
                      <Button
                        startIcon={<span className="icon-pencil" />}
                        outline
                        onClick={() => {
                          stationsModal.show({ index: i });
                          fetchStations({ reset: true, perPage: 10 });
                        }}
                      >
                        {locale.selectStations}
                      </Button>
                      <Button
                        startIcon={<GetAppRoundedIcon />}
                        outline
                        onClick={() => {
                          importStationModal.show({ index: i });
                          fetchStations({ reset: true, perPage: 1000 });
                        }}
                      >
                        {locale.importCSV}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        startIcon={<span className="icon-pencil" />}
                        outline
                        onClick={() => {
                          stationsModal.show({ index: i });
                          setStationInputs(item?.stationIds?.value);
                          fetchStations({ reset: true, perPage: 10 });
                        }}
                      >
                        {locale.editStations}
                      </Button>
                      <Button
                        startIcon={<GetAppRoundedIcon />}
                        outline
                        onClick={() => {
                          importStationModal.show({ index: i });
                          setStationInputs(item.stationIds?.value);
                          fetchStations({ reset: true, perPage: 1000 });
                        }}
                      >
                        {locale.importCSV}
                      </Button>
                    </>
                  )
                ) : (
                  <Text className={styles.discountCombinationLabel}>
                    {locale.discountCombination}
                  </Text>
                )}

                {editMode ? (
                  <div className={styles.stationCount}>
                    <span className={styles.numberStationSelected}>{stationText}</span>
                  </div>
                ) : (
                  <PopOverList list={item?.stationIds?.value?.map((s) => s.label)}>
                    <Button link className={styles.stationLink}>
                      {stationText}
                    </Button>
                  </PopOverList>
                )}

                {discountField(item, i, "discountDiesel", locale.diesel)}
                {discountField(item, i, "discountGas91", locale.gas91)}
                {discountField(item, i, "discountGas95", locale.gas95)}
                {discountField(item, i, "discountGas97", locale.gas97)}

                {editMode && (
                  <span onClick={() => onRemoveRow(i)} className={styles.removeCohort}>
                    <Image src={RemoveIcon} />
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {editMode && (
          <Button outline className={styles.continue} onClick={addRow}>
            {locale.addNewDiscountCombination}
          </Button>
        )}
      </div>
    );
  }
);

export default ContractDiscounts;
